import { useState, useEffect, useRef } from 'react'
import { modal } from '../../lib/Z'
import { getLettersFromText } from '../../lib/pairings'

type ManualEntryModalProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (wordAText: string, wordBText: string, clue?: string) => void
  requiredLength?: number | null
  existingLettersA?: string  // Pre-fill from crossing constraints
  existingLettersB?: string
}

export function ManualEntryModal({
  isOpen,
  onClose,
  onSave,
  requiredLength,
  existingLettersA = '',
  existingLettersB = ''
}: ManualEntryModalProps) {
  const [wordA, setWordA] = useState('')
  const [wordB, setWordB] = useState('')
  const [clue, setClue] = useState('')
  const [error, setError] = useState<string | null>(null)

  const wordARef = useRef<HTMLInputElement>(null)

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setWordA(existingLettersA)
      setWordB(existingLettersB)
      setClue('')
      setError(null)
      // Focus first input
      setTimeout(() => wordARef.current?.focus(), 50)
    }
  }, [isOpen, existingLettersA, existingLettersB])

  if (!isOpen) return null

  const lettersA = getLettersFromText(wordA)
  const lettersB = getLettersFromText(wordB)

  const validate = (): string | null => {
    if (!wordA.trim() && !wordB.trim()) {
      return 'Enter at least one word'
    }

    // If only one word is entered, allow it (will use placeholder)
    const effectiveA = wordA.trim() || '□'.repeat(lettersB.length || 1)
    const effectiveB = wordB.trim() || '□'.repeat(lettersA.length || 1)

    const effectiveLettersA = effectiveA.replace(/\s/g, '')
    const effectiveLettersB = effectiveB.replace(/\s/g, '')

    if (effectiveLettersA.length !== effectiveLettersB.length) {
      return `Words must be same length (${effectiveLettersA.length} vs ${effectiveLettersB.length})`
    }

    if (requiredLength !== null && effectiveLettersA.length !== requiredLength) {
      return `Word must be ${requiredLength} letters (got ${effectiveLettersA.length})`
    }

    return null
  }

  const handleSave = () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    // If one side is empty, use placeholder
    const finalWordA = wordA.trim() || '□'.repeat(lettersB.length)
    const finalWordB = wordB.trim() || '□'.repeat(lettersA.length)

    onSave(finalWordA, finalWordB, clue.trim() || undefined)
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSave()
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center"
      style={{ zIndex: modal }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="font-medium text-gray-900">Manual Entry</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Add a custom word pair to the grid
          </p>
        </div>

        {/* Form */}
        <div className="p-4 space-y-4">
          {/* Word A */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Word A (List A)
            </label>
            <input
              ref={wordARef}
              type="text"
              value={wordA}
              onChange={e => {
                setWordA(e.target.value.toUpperCase())
                setError(null)
              }}
              placeholder={wordB ? '□'.repeat(lettersB.length || 4) : 'WORD'}
              className="w-full px-3 py-2 font-mono text-sm uppercase border border-gray-200 rounded focus:outline-none focus:border-gray-400"
            />
          </div>

          {/* Word B */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Word B (List B)
            </label>
            <input
              type="text"
              value={wordB}
              onChange={e => {
                setWordB(e.target.value.toUpperCase())
                setError(null)
              }}
              placeholder={wordA ? '□'.repeat(lettersA.length || 4) : 'WORD'}
              className="w-full px-3 py-2 font-mono text-sm uppercase border border-gray-200 rounded focus:outline-none focus:border-gray-400"
            />
          </div>

          {/* Length indicator */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              Length: {lettersA.length || lettersB.length || 0}
              {requiredLength !== null && ` (required: ${requiredLength})`}
            </span>
            {lettersA.length > 0 && lettersB.length > 0 && lettersA.length !== lettersB.length && (
              <span className="text-red-500">
                Mismatch!
              </span>
            )}
          </div>

          {/* Clue (optional) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Clue (optional)
            </label>
            <input
              type="text"
              value={clue}
              onChange={e => setClue(e.target.value)}
              placeholder="Enter a clue..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-gray-400"
            />
          </div>

          {/* Placeholder note */}
          {(!wordA.trim() || !wordB.trim()) && (wordA.trim() || wordB.trim()) && (
            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
              Note: Empty side will be saved as "{('□').repeat(Math.max(lettersA.length, lettersB.length) || 4)}" placeholder
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-1.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded transition-colors"
          >
            Add to Grid
          </button>
        </div>
      </div>
    </div>
  )
}
